import React from 'react';
import { ArrowRight, Users, XCircle, Clock3 } from 'lucide-react';
import './VoucherUsageTab.css';

// ================== DATA (استبدلها بالداتا من الـ API) ==================
const assignmentSummary = [
  { label: 'Assigned Users', value: '1,450' },
  { label: 'Eligible Users', value: '3,200' },
  { label: 'Used Users', value: '982' },
  { label: 'Unused Assigned', value: '468' },
];
const assignmentProgress = { pct: 67.7, caption: '67.7% of assigned users redeemed this voucher.' };

const usageLimits = [
  { label: 'Per User Limit', value: '1 time' },
  { label: 'Total Voucher Limit', value: '2,000 redemptions' },
  { label: 'Current Usage', value: '1,284 / 2,000' },
  { label: 'Remaining Usage', value: '716' },
];
const usageProgress = { pct: 62, caption: '62% used' };

const assignmentMethod = {
  badge: 'Auto Assignment',
  title: 'Assigned Automatically',
  desc: 'Assigned to VIP users based on loyalty tier and order history.',
  fields: [
    { label: 'Segment:', value: 'VIP Customers' },
    { label: 'Trigger:', value: 'Loyalty Level' },
    { label: 'Created by:', value: 'Admin' },
  ],
  fields2: [
    { label: 'Assignment date:', value: 'Mar 10, 2025' },
    { label: 'Last Updated:', value: 'May 18, 2025' },
  ],
};

const usageBySegment = [
  { label: 'VIP Customers', value: 860, color: '#14B8A6' },
  { label: 'High Spend Users', value: 310, color: '#3B82F6' },
  { label: 'Returning Users', value: 114, color: '#8B5CF6' },
];

const assignedSegments = [
  { name: 'VIP Customers', users: '1,200', eligible: '1,100', redeemed: '860', rate: '78.1%', status: 'Active' },
  { name: 'High Spend Users', users: '850', eligible: '760', redeemed: '310', rate: '40.8%', status: 'Active' },
  { name: 'Returning Users', users: '500', eligible: '430', redeemed: '114', rate: '26.5%', status: 'Active' },
];

const eligibilityStatus = 'Eligibility Active';
const eligibilityStats = [
  { icon: Users, color: '#8B5CF6', label: 'Eligible Users', value: '3,200' },
  { icon: XCircle, color: '#DC2626', label: 'Excluded Users', value: '420' },
  { icon: Clock3, color: '#F5A623', label: 'Pending Check', value: '85' },
];

const topSegments = [
  { label: 'VIP Customers', value: '1,200' },
  { label: 'High Spend Users', value: '850' },
  { label: 'Returning Users', value: '500' },
  { label: 'Inactive Win-back', value: '320' },
];

const usageInsights = [
  'VIP segment has the highest usage rate.',
  '468 assigned users have not redeemed yet.',
  'Usage limit is still safe.',
  'Consider reminder notification for unused users.',
];

// ================== SUB COMPONENTS ==================

function StepBadge({ n }) {
  return <span className="vu-step-badge">{n}</span>;
}

function ProgressBar({ pct, color }) {
  return (
    <div className="vu-progress-track">
      <div className="vu-progress-fill" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

function BarChart({ data }) {
  const width = 700;
  const height = 260;
  const padLeft = 36;
  const padBottom = 50;
  const maxY = 1000;
  const gridValues = [0, 250, 500, 750, 1000];

  const plotW = width - padLeft;
  const plotH = height - padBottom;
  const barWidth = 90;
  const gap = (plotW - data.length * barWidth) / (data.length + 1);

  return (
    <div className="vu-chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="vu-chart-svg"
        preserveAspectRatio="none"
      >
        {gridValues.map((g) => {
          const y = plotH - (g / maxY) * plotH;
          return (
            <g key={g}>
              <line
                x1={padLeft}
                x2={width}
                y1={y}
                y2={y}
                className="vu-grid-line"
              />
              <text
                x={padLeft - 10}
                y={y + 4}
                className="vu-axis-label"
                textAnchor="end"
              >
                {g}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const barH = (d.value / maxY) * plotH;
          const x = padLeft + gap + i * (barWidth + gap);
          const y = plotH - barH;
          return (
            <g key={d.label}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                rx="6"
                fill={d.color}
              />
              <text
                x={x + barWidth / 2}
                y={plotH + 20}
                className="vu-bar-value"
                textAnchor="middle"
              >
                {d.value}
              </text>
              <text
                x={x + barWidth / 2}
                y={plotH + 38}
                className="vu-bar-label"
                textAnchor="middle"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ================== MAIN COMPONENT ==================
export default function VoucherUsageTab() {
  return (
    <div className="vu-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="vu-main">
        <div className="vu-panel">
          {/* 1. Assignment Summary */}
          <div className="vu-panel-section">
            <div className="vu-card-header">
              <StepBadge n={1} />
              <h3 className="vu-card-title">Assignment Summary</h3>
            </div>

            <div className="vu-field-grid">
              {assignmentSummary.map((f) => (
                <div className="vu-field-item" key={f.label}>
                  <span className="vu-field-label">{f.label}</span>
                  <span className="vu-field-value">{f.value}</span>
                </div>
              ))}
            </div>

            <ProgressBar pct={assignmentProgress.pct} color="#14B8A6" />
            <p className="vu-progress-caption">{assignmentProgress.caption}</p>
          </div>
          <div className="vu-panel-divider" />
          {/* 2. Usage Limits */}
          <div className="vu-panel-section">
            <div className="vu-card-header">
              <StepBadge n={2} />
              <h3 className="vu-card-title">Usage Limits</h3>
            </div>

            <div className="vu-field-grid">
              {usageLimits.map((f) => (
                <div className="vu-field-item" key={f.label}>
                  <span className="vu-field-label">{f.label}</span>
                  <span className="vu-field-value">{f.value}</span>
                </div>
              ))}
            </div>

            <ProgressBar pct={usageProgress.pct} color="#7E1BAD" />
            <p className="vu-progress-caption">{usageProgress.caption}</p>
          </div>
          <div className="vu-panel-divider" />
          {/* 3. Assignment Method */}
          <div className="vu-panel-section">
            <div className="vu-card-header-row">
              <div className="vu-card-header">
                <StepBadge n={3} />
                <h3 className="vu-card-title">Assignment Method</h3>
              </div>
              <span className="vu-badge-pill">{assignmentMethod.badge}</span>
            </div>

            <h4 className="vu-sub-title">{assignmentMethod.title}</h4>
            <p className="vu-desc">{assignmentMethod.desc}</p>

            <div className="vu-inline-fields">
              {assignmentMethod.fields.map((f) => (
                <span className="vu-inline-field" key={f.label}>
                  <span className="vu-inline-label">{f.label}</span> <span className="vu-inline-value">{f.value}</span>
                </span>
              ))}
            </div>
            <div className="vu-inline-fields">
              {assignmentMethod.fields2.map((f) => (
                <span className="vu-inline-field" key={f.label}>
                  <span className="vu-inline-label">{f.label}</span> <span className="vu-inline-value">{f.value}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="vu-panel-divider" />
          {/* 4. Usage by Segment */}
          <div className="vu-panel-section">
            <div className="vu-card-header mb-4">
              <StepBadge n={4} />
              <h3 className="vu-card-title">Usage by Segment</h3>
            </div>

            <BarChart data={usageBySegment} />
          </div>
          <div className="vu-panel-divider" />
          {/* 5. Assigned Segments */}
          <div className="vu-panel-section">
            <div className="vu-card-header-row">
              <div className="vu-card-header">
                <StepBadge n={5} />
                <h3 className="vu-card-title">Assigned Segments</h3>
              </div>
              <a className="vu-link" href="#">View all <ArrowRight size={14} /></a>
            </div>

            <div className="vu-table-wrap">
              <table className="vu-table">
                <thead>
                  <tr>
                    <th>Segment Name</th>
                    <th>Users</th>
                    <th>Eligible</th>
                    <th>Redeemed</th>
                    <th>Usage Rate</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedSegments.map((s) => (
                    <tr key={s.name}>
                      <td>{s.name}</td>
                      <td>{s.users}</td>
                      <td>{s.eligible}</td>
                      <td>{s.redeemed}</td>
                      <td>{s.rate}</td>
                      <td><span className="vu-status-pill">{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* ---------- SIDEBAR ---------- */}
      <div className="vu-sidebar">
        {/* Eligibility Status */}
        <div className="vu-card">
          <div className="vu-card-header-row">
            <h3 className="vu-card-title">Eligibility Status</h3>
            <span className="vu-badge-pill">{eligibilityStatus}</span>
          </div>
          <div className="vu-list">
            {eligibilityStats.map((s) => {
              const Icon = s.icon;
              return (
                <div className="vu-list-item" key={s.label}>
                  <span className="vu-list-icon" style={{ color: s.color }}>
                    <Icon size={16} fill={s.color} color="#fff" />
                  </span>
                  <span className="vu-list-label">{s.label}</span>
                  <span className="vu-list-value">{s.value}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Assigned Segments */}
        <div className="vu-card">
          <div className="vu-card-header-row">
            <h3 className="vu-card-title">Top Assigned Segments</h3>
            <a className="vu-link" href="#">View Full Assignment <ArrowRight size={14} /></a>
          </div>
          <div className="vu-segments-list">
            {topSegments.map((s, i) => (
              <div className="vu-segment-item" key={s.label}>
                <span className="vu-segment-num">{i + 1}</span>
                <span className="vu-segment-label">{s.label}</span>
                <span className="vu-segment-value">{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Insights */}
        <div className="vu-card">
          <div className="vu-card-header-row">
            <h3 className="vu-card-title">Usage Insights</h3>
            <a className="vu-link" href="#">View More Insights <ArrowRight size={14} /></a>
          </div>
          <ul className="vu-insights-list">
            {usageInsights.map((text, i) => (
              <li key={i} className="vu-insight-item">
                <span className="vu-insight-dot" />
                <span className="vu-insight-text">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}